import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, Phone, MapPin, Calendar, User, Download, Share2,
  AlertTriangle, Users, FileText, MessageCircle, Send, CheckCircle
} from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import PrintPoster from '../components/PrintPoster';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const CaseDetailPage = () => {
  const { caseId } = useParams();
  const [caseData, setCaseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showPoster, setShowPoster] = useState(false);
  const [tips, setTips] = useState([]);
  const [tipText, setTipText] = useState('');
  const [tipName, setTipName] = useState('');
  const [tipMobile, setTipMobile] = useState('');
  const [tipLoading, setTipLoading] = useState(false);

  useEffect(() => { fetchCaseDetail(); fetchTips(); }, [caseId]);

  const fetchCaseDetail = async () => {
    try {
      const response = await axios.get(`${API}/api/cases/${caseId}`);
      setCaseData(response.data);
    } catch (err) { setError('केस नहीं मिला'); }
    finally { setLoading(false); }
  };

  const fetchTips = async () => {
    try { const res = await axios.get(`${API}/api/cases/${caseId}/tips`); setTips(res.data.tips); }
    catch (err) { /* ignore */ }
  };

  const submitTip = async () => {
    if (!tipText.trim()) return;
    setTipLoading(true);
    try {
      await axios.post(`${API}/api/cases/${caseId}/tips`, { tip_text: tipText, tipper_name: tipName, tipper_mobile: tipMobile });
      toast.success('जानकारी सबमिट हो गई!');
      setTipText(''); setTipName(''); setTipMobile('');
      fetchTips();
    } catch (err) { toast.error('Error submitting tip'); }
    finally { setTipLoading(false); }
  };

  const getPhotoUrl = (path) => path ? (path.startsWith('http') ? path : `${API}/api/files/${path}`) : null;
  const genderMap = { 'male': 'पुरुष', 'female': 'महिला', 'other': 'अन्य' };

  // Share URLs - use current domain for sharing
  const shareUrl = `${window.location.origin}/api/share/${caseId}`;
  const caseUrl = `${window.location.origin}/case/${caseId}`;

  const getShareText = () => {
    if (!caseData) return '';
    const type = caseData.case_type === 'missing' ? 'लापता' : 'अज्ञात व्यक्ति';
    const loc = caseData.last_seen_location || caseData.found_location || '';
    const date = caseData.last_seen_date || caseData.found_date || '';
    const gender = genderMap[caseData.gender] || caseData.gender;
    let text = `🔴 *${type}*\n\n`;
    text += `*नाम:* ${caseData.name}\n`;
    text += `*उम्र:* ${caseData.age} वर्ष | *लिंग:* ${gender}\n`;
    if (caseData.height) text += `*कद:* ${caseData.height}\n`;
    if (caseData.complexion) text += `*रंग:* ${caseData.complexion}\n`;
    if (caseData.identifying_marks) text += `*पहचान चिह्न:* ${caseData.identifying_marks}\n`;
    if (caseData.clothing) text += `*कपड़े:* ${caseData.clothing}\n`;
    text += `\n*स्थान:* ${loc}, ${caseData.city}, ${caseData.state}\n`;
    text += `*तारीख:* ${date}\n`;
    if (caseData.police_station) text += `*थाना:* ${caseData.police_station}\n`;
    text += `*संपर्क:* ${caseData.reporter_mobile}\n`;
    if (caseData.additional_info) text += `\n_${caseData.additional_info}_\n`;
    text += `\nकृपया इस व्यक्ति को खोजने में मदद करें:\n${shareUrl}`;
    return text;
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin w-8 h-8 border-4 border-red-600 border-t-transparent rounded-full"></div></div>;
  if (error || !caseData) return <div className="min-h-screen flex flex-col items-center justify-center p-4"><AlertTriangle className="w-16 h-16 text-gray-300 mb-4" /><p className="text-gray-600 text-lg mb-4">{error || 'केस नहीं मिला'}</p><Link to="/"><Button variant="outline" className="rounded-full">होम पर जाएं</Button></Link></div>;

  const isMissing = caseData.case_type === 'missing';
  const bgColor = isMissing ? '#DC2626' : '#D97706';
  const isFound = caseData.status === 'found';

  return (
    <div className="pb-24 md:pb-12" data-testid="case-detail-page">
      {/* Found Banner */}
      {isFound && (
        <div className="bg-green-500 text-white text-center py-3 font-medium flex items-center justify-center gap-2">
          <CheckCircle className="w-5 h-5" /> यह व्यक्ति मिल गया है!
        </div>
      )}

      {/* ===== MOBILE LAYOUT ===== */}
      <div className="md:hidden">
        <div style={{ backgroundColor: bgColor }}>
          <div className="absolute top-4 left-4 z-10">
            <Link to={isMissing ? '/missing' : '/unknown'}><Button variant="ghost" size="icon" className="bg-black/20 text-white hover:bg-black/30 rounded-full" data-testid="back-btn"><ArrowLeft className="w-5 h-5" /></Button></Link>
          </div>
          <div className="absolute top-4 right-4 z-10"><span className="px-3 py-1.5 bg-black/20 text-white text-xs font-mono rounded-full">{caseData.case_id}</span></div>
          <div className="pt-16 pb-6 px-6 flex justify-center">
            <div className="w-48 h-48 rounded-2xl overflow-hidden border-4 border-white shadow-2xl bg-white">
              {caseData.photo_url ? <img src={getPhotoUrl(caseData.photo_url)} alt={caseData.name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center bg-gray-100"><User className="w-20 h-20 text-gray-300" /></div>}
            </div>
          </div>
        </div>
        <div className="px-4 -mt-4">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="app-card p-6 text-center">
            <h1 className="font-outfit font-bold text-2xl text-gray-900 mb-2">{caseData.name}</h1>
            <div className="flex items-center justify-center gap-4 text-sm text-gray-500">
              <span>{caseData.age} वर्ष</span><span>•</span><span>{genderMap[caseData.gender] || caseData.gender}</span>
            </div>
          </motion.div>
        </div>
        <div className="px-4 mt-4 space-y-2">
          <Button onClick={() => setShowPoster(true)} className={`rounded-full h-12 w-full ${isMissing ? 'bg-red-600 hover:bg-red-700' : 'bg-amber-600 hover:bg-amber-700'}`} data-testid="download-poster-btn"><Download className="w-4 h-4 mr-2" />पोस्टर डाउनलोड</Button>
          <div className="grid grid-cols-2 gap-2">
            <Button variant="outline" className="rounded-full h-12 w-full border-green-500 text-green-600 hover:bg-green-50" data-testid="share-whatsapp" onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(getShareText())}`, '_blank')}><MessageCircle className="w-4 h-4 mr-1" />WhatsApp</Button>
            <Button variant="outline" className="rounded-full h-12 w-full border-blue-500 text-blue-600 hover:bg-blue-50" data-testid="share-facebook-mobile" onClick={() => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, '_blank')}><Share2 className="w-4 h-4 mr-1" />Facebook</Button>
          </div>
        </div>
        <MobileInfoSections caseData={caseData} isMissing={isMissing} genderMap={genderMap} />
        {/* Mobile Tips */}
        <div className="px-4 mt-6"><TipsSection tips={tips} tipText={tipText} setTipText={setTipText} tipName={tipName} setTipName={setTipName} tipMobile={tipMobile} setTipMobile={setTipMobile} tipLoading={tipLoading} submitTip={submitTip} /></div>
      </div>

      {/* ===== DESKTOP LAYOUT ===== */}
      <div className="hidden md:block py-8">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex items-center gap-2 mb-6 text-sm text-gray-500">
            <Link to="/" className="hover:text-gray-900 transition-colors">होम</Link><span>/</span>
            <Link to={isMissing ? '/missing' : '/unknown'} className="hover:text-gray-900 transition-colors">{isMissing ? 'लापता' : 'अज्ञात'}</Link><span>/</span>
            <span className="text-gray-900 font-medium">{caseData.name}</span>
          </div>
          <div className="grid lg:grid-cols-3 gap-8">
            <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} className="lg:col-span-1">
              <div className="app-card overflow-hidden sticky top-24">
                <div className="aspect-[3/4] bg-gray-100 relative">
                  {caseData.photo_url ? <img src={getPhotoUrl(caseData.photo_url)} alt={caseData.name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><User className="w-24 h-24 text-gray-300" /></div>}
                  <div className="absolute top-3 left-3"><span className="status-badge-glass px-3 py-1.5 text-xs font-bold rounded-full">{isMissing ? 'लापता' : 'अज्ञात'}</span></div>
                  {isFound && <div className="absolute top-3 right-3"><span className="px-3 py-1.5 text-xs font-bold rounded-full bg-green-500 text-white">मिल गया</span></div>}
                </div>
                <div className="p-4 space-y-3">
                  <Button onClick={() => setShowPoster(true)} className={`w-full rounded-xl h-12 ${isMissing ? 'bg-red-600 hover:bg-red-700' : 'bg-amber-600 hover:bg-amber-700'} btn-animated`} data-testid="download-poster-btn-desktop"><Download className="w-4 h-4 mr-2" />पोस्टर डाउनलोड</Button>
                  <a href={`https://wa.me/?text=${encodeURIComponent(getShareText())}`} target="_blank" rel="noopener noreferrer" className="block">
                    <Button variant="outline" className="w-full rounded-xl h-12 border-green-500 text-green-600 hover:bg-green-50"><MessageCircle className="w-4 h-4 mr-2" />WhatsApp शेयर</Button>
                  </a>
                  <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`} target="_blank" rel="noopener noreferrer" className="block">
                    <Button variant="outline" className="w-full rounded-xl h-12 border-blue-500 text-blue-600 hover:bg-blue-50"><Share2 className="w-4 h-4 mr-2" />Facebook शेयर</Button>
                  </a>
                  {/* QR Code */}
                  <div className="pt-3 border-t border-gray-100 flex flex-col items-center">
                    <p className="text-xs text-gray-500 mb-2">QR कोड स्कैन करें</p>
                    <QRCodeSVG value={caseUrl} size={120} level="M" />
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="lg:col-span-2 space-y-6">
              {/* Name */}
              <div className="app-card p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold mb-3 ${isMissing ? 'badge-missing' : 'badge-unknown'}`}>{isMissing ? <><AlertTriangle className="w-3 h-3" />लापता</> : <><Users className="w-3 h-3" />अज्ञात</>}</span>
                    {isFound && <span className="ml-2 inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold bg-green-100 text-green-700"><CheckCircle className="w-3 h-3" />मिल गया</span>}
                    <h1 className="font-outfit font-bold text-3xl text-gray-900">{caseData.name}</h1>
                  </div>
                  <span className="px-3 py-1.5 bg-gray-100 text-gray-600 text-xs font-mono rounded-lg">{caseData.case_id}</span>
                </div>
                <div className="flex items-center gap-6 text-gray-600">
                  <span className="flex items-center gap-2"><User className="w-4 h-4 text-gray-400" />{caseData.age} वर्ष, {genderMap[caseData.gender] || caseData.gender}</span>
                  {caseData.height && <span>{caseData.height}</span>}
                  {caseData.complexion && <span>{caseData.complexion}</span>}
                </div>
              </div>

              {/* Location */}
              <div className="app-card p-6">
                <h3 className="font-outfit font-semibold text-lg text-gray-900 mb-4 flex items-center gap-2"><MapPin className="w-5 h-5 text-gray-400" />{isMissing ? 'अंतिम बार देखा गया' : 'पाया गया'}</h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  <InfoRow label="स्थान" value={isMissing ? caseData.last_seen_location : caseData.found_location} />
                  <InfoRow label="तारीख" value={isMissing ? caseData.last_seen_date : caseData.found_date} />
                  <InfoRow label="समय" value={isMissing ? caseData.last_seen_time : caseData.found_time} />
                  <InfoRow label="शहर, राज्य" value={`${caseData.city}, ${caseData.state}`} />
                </div>
              </div>

              {/* Physical */}
              {(caseData.complexion || caseData.identifying_marks || caseData.physical_details || caseData.clothing || caseData.height) && (
                <div className="app-card p-6">
                  <h3 className="font-outfit font-semibold text-lg text-gray-900 mb-4 flex items-center gap-2"><User className="w-5 h-5 text-gray-400" />शारीरिक विवरण</h3>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <InfoRow label="ऊंचाई" value={caseData.height} />
                    <InfoRow label="रंग" value={caseData.complexion} />
                    <InfoRow label="पहचान चिह्न" value={caseData.identifying_marks} />
                    <InfoRow label="अन्य विवरण" value={caseData.physical_details} />
                    <InfoRow label="पहनावा" value={caseData.clothing} />
                  </div>
                </div>
              )}

              {/* Contact */}
              <div className={`app-card p-6 ${isMissing ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'}`}>
                <h3 className="font-outfit font-semibold text-lg text-gray-900 mb-4 flex items-center gap-2"><Phone className="w-5 h-5 text-gray-400" />संपर्क करें</h3>
                <p className="text-sm text-gray-600 mb-4">यदि आपके पास इस व्यक्ति के बारे में कोई जानकारी है तो कृपया तुरंत संपर्क करें</p>
                <a href={`tel:${caseData.reporter_mobile}`} className={`inline-flex items-center gap-3 px-8 py-4 rounded-2xl font-bold text-lg text-white ${isMissing ? 'bg-red-600 hover:bg-red-700' : 'bg-amber-600 hover:bg-amber-700'} transition-colors`} data-testid="call-contact"><Phone className="w-5 h-5" />{caseData.reporter_mobile}</a>
                <p className="text-sm text-gray-600 mt-3">{caseData.reporter_name}{caseData.reporter_relation && ` (${caseData.reporter_relation})`}</p>
              </div>

              {/* Reporter */}
              <div className="app-card p-6">
                <h3 className="font-outfit font-semibold text-lg text-gray-900 mb-4 flex items-center gap-2"><FileText className="w-5 h-5 text-gray-400" />रिपोर्टर जानकारी</h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  <InfoRow label="नाम" value={caseData.reporter_name} />
                  <InfoRow label="मोबाइल" value={caseData.reporter_mobile} />
                  {caseData.reporter_email && <InfoRow label="ईमेल" value={caseData.reporter_email} />}
                  {caseData.reporter_relation && <InfoRow label="संबंध" value={caseData.reporter_relation} />}
                  {caseData.police_station && <InfoRow label="पुलिस स्टेशन" value={caseData.police_station} />}
                </div>
              </div>

              {/* Tips */}
              <TipsSection tips={tips} tipText={tipText} setTipText={setTipText} tipName={tipName} setTipName={setTipName} tipMobile={tipMobile} setTipMobile={setTipMobile} tipLoading={tipLoading} submitTip={submitTip} />
            </motion.div>
          </div>
        </div>
      </div>

      {showPoster && <PrintPoster caseData={caseData} onClose={() => setShowPoster(false)} />}
    </div>
  );
};

const InfoRow = ({ label, value }) => { if (!value) return null; return <div><span className="text-sm text-gray-500 block mb-1">{label}</span><span className="text-sm font-medium text-gray-900">{value}</span></div>; };

const TipsSection = ({ tips, tipText, setTipText, tipName, setTipName, tipMobile, setTipMobile, tipLoading, submitTip }) => (
  <div className="app-card p-6" data-testid="tips-section">
    <h3 className="font-outfit font-semibold text-lg text-gray-900 mb-4 flex items-center gap-2"><MessageCircle className="w-5 h-5 text-gray-400" />जानकारी दें (Tips)</h3>
    <div className="space-y-3 mb-4">
      <Input value={tipName} onChange={(e) => setTipName(e.target.value)} placeholder="आपका नाम (वैकल्पिक)" className="rounded-xl" data-testid="tip-name" />
      <Input value={tipMobile} onChange={(e) => setTipMobile(e.target.value)} placeholder="मोबाइल नंबर (वैकल्पिक)" className="rounded-xl" data-testid="tip-mobile" />
      <textarea value={tipText} onChange={(e) => setTipText(e.target.value)} rows={3} placeholder="आपकी जानकारी यहाँ लिखें..." className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500" data-testid="tip-text" />
      <Button onClick={submitTip} disabled={tipLoading || !tipText.trim()} className="rounded-xl bg-gray-900 hover:bg-gray-800" data-testid="submit-tip-btn"><Send className="w-4 h-4 mr-2" />जानकारी भेजें</Button>
    </div>
    {tips.length > 0 && (
      <div className="mt-4 space-y-3 border-t border-gray-100 pt-4">
        <p className="text-sm text-gray-500 font-medium">{tips.length} जानकारी मिली</p>
        {tips.map((tip) => (
          <div key={tip.tip_id} className="p-3 bg-gray-50 rounded-xl">
            <p className="text-sm text-gray-800">{tip.tip_text}</p>
            <p className="text-xs text-gray-500 mt-1">{tip.tipper_name} • {new Date(tip.created_at).toLocaleDateString('hi-IN')}</p>
          </div>
        ))}
      </div>
    )}
  </div>
);

const MobileInfoSections = ({ caseData, isMissing, genderMap }) => (
  <>
    <div className="px-4 mt-6">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="app-card p-5">
        <h3 className="font-outfit font-semibold text-gray-900 mb-4 flex items-center gap-2"><MapPin className="w-5 h-5 text-gray-400" />{isMissing ? 'अंतिम बार देखा गया' : 'पाया गया'}</h3>
        <div className="space-y-3">
          <MobileRow label="स्थान" value={isMissing ? caseData.last_seen_location : caseData.found_location} />
          <MobileRow label="तारीख" value={isMissing ? caseData.last_seen_date : caseData.found_date} />
          <MobileRow label="शहर, राज्य" value={`${caseData.city}, ${caseData.state}`} />
        </div>
      </motion.div>
    </div>
    {(caseData.complexion || caseData.identifying_marks || caseData.physical_details || caseData.clothing || caseData.height) && (
      <div className="px-4 mt-4">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="app-card p-5">
          <h3 className="font-outfit font-semibold text-gray-900 mb-4 flex items-center gap-2"><User className="w-5 h-5 text-gray-400" />शारीरिक विवरण</h3>
          <div className="space-y-3">
            {caseData.height && <MobileRow label="ऊंचाई" value={caseData.height} />}
            {caseData.complexion && <MobileRow label="रंग" value={caseData.complexion} />}
            {caseData.identifying_marks && <div><span className="text-sm text-gray-500 block mb-1">पहचान चिह्न</span><span className="text-sm font-medium text-gray-900">{caseData.identifying_marks}</span></div>}
            {caseData.clothing && <MobileRow label="पहनावा" value={caseData.clothing} />}
          </div>
        </motion.div>
      </div>
    )}
    <div className="px-4 mt-4">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className={`app-card p-5 ${isMissing ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'}`}>
        <h3 className="font-outfit font-semibold text-gray-900 mb-4 flex items-center gap-2"><Phone className="w-5 h-5 text-gray-400" />संपर्क करें</h3>
        <a href={`tel:${caseData.reporter_mobile}`} className={`flex items-center justify-center gap-3 py-4 rounded-2xl font-bold text-lg text-white ${isMissing ? 'bg-red-600' : 'bg-amber-600'}`} data-testid="call-contact"><Phone className="w-5 h-5" />{caseData.reporter_mobile}</a>
        <div className="mt-3 text-center"><p className="text-sm text-gray-600">{caseData.reporter_name}{caseData.reporter_relation && ` (${caseData.reporter_relation})`}</p></div>
      </motion.div>
    </div>
  </>
);

const MobileRow = ({ label, value }) => { if (!value) return null; return <div className="flex justify-between"><span className="text-sm text-gray-500">{label}</span><span className="text-sm font-medium text-gray-900 text-right max-w-[60%]">{value}</span></div>; };

export default CaseDetailPage;
